import { supabase } from '@/integrations/supabase/client';

export interface CreateNotificationParams {
  userId: string;
  organizationUuid: string;
  type: 'job_update' | 'new_application' | 'application_status_change' | 'candidate_update' | 'comment_mention' | 'system' | 'ai_processing_complete';
  title: string;
  message: string;
  data?: any;
}

export interface Notification {
  id: string;
  user_id: string;
  organization_uuid: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export const NotificationService = {
  /**
   * Create a new notification
   */
  createNotification: async (params: CreateNotificationParams): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .rpc('create_notification', {
          p_user_id: params.userId,
          p_organization_uuid: params.organizationUuid,
          p_type: params.type,
          p_title: params.title,
          p_message: params.message,
          p_data: params.data || null,
        });

      if (error) {
        console.error('Error creating notification:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to create notification:', error);
      return null;
    }
  },

  /**
   * Create notifications for all users in an organization
   */
  createOrganizationNotification: async (
    organizationUuid: string,
    type: string,
    title: string,
    message: string,
    data?: any
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .rpc('create_org_notifications', {
          org_uuid: organizationUuid,
          notification_type: type,
          notification_title: title,
          notification_message: message,
          notification_data: data || null,
        });

      if (error) {
        console.error('Error creating organization notification:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to create organization notification:', error);
      return false;
    }
  },

  /**
   * Get unread notification count for the current user
   */
  getUnreadCount: async (): Promise<number> => {
    try {
      const { data, error } = await supabase
        .rpc('get_unread_notifications_count');

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return data || 0;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      return 0;
    }
  },

  /**
   * Mark notifications as read
   */
  markAsRead: async (notificationIds: string[]): Promise<boolean> => {
    try {
      const { error } = await supabase
        .rpc('mark_notifications_read', { notification_ids: notificationIds });

      if (error) {
        console.error('Error marking notifications as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      return false;
    }
  },

  /**
   * Get recent notifications for the current user
   */
  getRecentNotifications: async (limit: number = 20): Promise<Notification[]> => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return [];
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching notifications:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return [];
    }
  },

  /**
   * Get all notifications for the current user with pagination
   */
  getAllNotifications: async (page: number = 1, pageSize: number = 50): Promise<{
    notifications: Notification[];
    total: number;
    hasMore: boolean;
  }> => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return { notifications: [], total: 0, hasMore: false };
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error fetching all notifications:', error);
        return { notifications: [], total: 0, hasMore: false };
      }

      const total = count || 0;
      const hasMore = total > to + 1;

      return {
        notifications: data || [],
        total,
        hasMore,
      };
    } catch (error) {
      console.error('Failed to fetch all notifications:', error);
      return { notifications: [], total: 0, hasMore: false };
    }
  },

  /**
   * Delete a notification (only for the current user)
   */
  deleteNotification: async (notificationId: string): Promise<boolean> => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return false;
      }

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id); // Ensure user can only delete their own notifications

      if (error) {
        console.error('Error deleting notification:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  },

  /**
   * Subscribe to real-time notifications with enhanced filtering
   */
  subscribeToNotifications: (
    userId: string, 
    callback: (notification: Notification) => void,
    options?: {
      organizationUuid?: string;
      types?: string[];
    }
  ) => {
    const channel = supabase
      .channel(`notifications-realtime-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: options?.organizationUuid 
            ? `organization_uuid=eq.${options.organizationUuid}`
            : undefined,
        },
        (payload) => {
          const notification = payload.new as Notification;
          
          // Filter by user if specified
          if (userId && notification.user_id !== userId) {
            return;
          }
          
          // Filter by type if specified
          if (options?.types && !options.types.includes(notification.type)) {
            return;
          }
          
          callback(notification);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: options?.organizationUuid 
            ? `organization_uuid=eq.${options.organizationUuid}`
            : undefined,
        },
        (payload) => {
          const notification = payload.new as Notification;
          
          // Filter by user if specified
          if (userId && notification.user_id !== userId) {
            return;
          }
          
          callback(notification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Helper function to create job-related notifications
   */
  createJobNotification: async (
    userId: string,
    organizationUuid: string,
    jobId: string,
    jobTitle: string,
    type: 'job_update' | 'new_application' | 'application_status_change',
    message: string,
    additionalData?: any
  ) => {
    const title = type === 'job_update' 
      ? `Job Updated: ${jobTitle}`
      : type === 'new_application'
      ? `New Application: ${jobTitle}`
      : `Application Status Changed: ${jobTitle}`;

    return NotificationService.createNotification({
      userId,
      organizationUuid,
      type,
      title,
      message,
      data: {
        entity_type: 'job',
        entity_id: jobId,
        job_title: jobTitle,
        ...additionalData,
      },
    });
  },

  /**
   * Helper function to create candidate-related notifications
   */
  createCandidateNotification: async (
    userId: string,
    organizationUuid: string,
    candidateId: string,
    candidateName: string,
    type: 'candidate_update',
    message: string,
    additionalData?: any
  ) => {
    return NotificationService.createNotification({
      userId,
      organizationUuid,
      type,
      title: `Candidate Updated: ${candidateName}`,
      message,
      data: {
        entity_type: 'candidate',
        entity_id: candidateId,
        candidate_name: candidateName,
        ...additionalData,
      },
    });
  },

  /**
   * Helper function to create comment mention notifications
   */
  createCommentMentionNotification: async (
    userId: string,
    organizationUuid: string,
    commentId: string,
    commenterName: string,
    entityType: string,
    entityId: string,
    entityName: string,
    message: string
  ) => {
    // Create enhanced comment mention message
    const mentionMessage = `You were mentioned by "${commenterName}" in a comment about "${entityName}".`;
    
    return supabase.rpc('create_org_notifications_with_subscriptions', {
      p_organization_uuid: organizationUuid,
      p_type: 'comment_mention',
      p_title: `You were mentioned by ${commenterName}`,
      p_message: mentionMessage,
      p_data: {
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        comment_id: commentId,
        commenter_name: commenterName,
        action: 'mentioned'
      },
      p_entity_type: entityType,
      p_entity_id: entityId
    });
  },

  /**
   * Helper function to create system notifications
   */
  createSystemNotification: async (
    userId: string,
    organizationUuid: string,
    title: string,
    message: string,
    additionalData?: any
  ) => {
    // For system notifications, we don't have a specific entity, so we use global settings
    return supabase.rpc('create_org_notifications_with_subscriptions', {
      p_organization_uuid: organizationUuid,
      p_type: 'system',
      p_title: title,
      p_message: message,
      p_data: additionalData || {},
      p_entity_type: null,
      p_entity_id: null
    });
  },

  /**
   * Helper function to create AI processing completion notifications
   */
  createAIProcessingNotification: async (
    userId: string,
    organizationUuid: string,
    entityType: string,
    entityId: string,
    entityName: string,
    analysisType: string,
    additionalData?: any
  ) => {
    // Create enhanced AI processing message
    const analysisMessage = `AI analysis (${analysisType}) has been completed for "${entityName}". The results are now available for review.`;
    
    return supabase.rpc('create_org_notifications_with_subscriptions', {
      p_organization_uuid: organizationUuid,
      p_type: 'ai_processing_complete',
      p_title: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Analysis Complete: ${entityName}`,
      p_message: analysisMessage,
      p_data: {
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        analysis_type: analysisType,
        action: 'analysis_complete',
        ...additionalData,
      },
      p_entity_type: entityType,
      p_entity_id: entityId
    });
  },

  /**
   * Bulk notification creation for multiple users
   */
  createBulkNotifications: async (
    userIds: string[],
    organizationUuid: string,
    type: string,
    title: string,
    message: string,
    data?: any,
    entityType?: string,
    entityId?: string
  ): Promise<boolean> => {
    try {
      // Use the subscription system for bulk notifications
      const { error } = await supabase.rpc('create_org_notifications_with_subscriptions', {
        p_organization_uuid: organizationUuid,
        p_type: type,
        p_title: title,
        p_message: message,
        p_data: data || {},
        p_entity_type: entityType || null,
        p_entity_id: entityId || null
      });

      if (error) {
        console.error('Error creating bulk notifications:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in createBulkNotifications:', error);
      return false;
    }
  },

  /**
   * Get notification statistics for the current user
   */
  getNotificationStats: async (): Promise<{
    total: number;
    unread: number;
    byType: Record<string, number>;
  }> => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return { total: 0, unread: 0, byType: {} };
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('type, read_at')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching notification stats:', error);
        return { total: 0, unread: 0, byType: {} };
      }

      const total = data?.length || 0;
      const unread = data?.filter(n => !n.read_at).length || 0;
      const byType: Record<string, number> = {};

      data?.forEach(notification => {
        byType[notification.type] = (byType[notification.type] || 0) + 1;
      });

      return { total, unread, byType };
    } catch (error) {
      console.error('Failed to fetch notification stats:', error);
      return { total: 0, unread: 0, byType: {} };
    }
  },
}; 